import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    FluentProvider,
    Input,
    Label,
    makeStyles,
    mergeClasses,
    useId,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import * as React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { useCurrentFluent2Theme } from '../../../../hooks/styling';
import { useStackStyles } from '../../../../themes/styles/flexbox-styles';

export interface ReplaceSnapshotDialogProps {
    onDismiss: () => void;
    onCancel: () => void;
    onReplaceSnapshotSubmitted: () => void;
    open: boolean;
    existingManualSnapshotName: string;
    newSnapshotName: string;
}

const messages = defineMessages({
    replaceSnapshotButtonAriaLabel: {
        id: 'ReplaceSnapshotDialog_Button_AriaLabel',
        defaultMessage: 'Replace snapshot',
        description: 'Aria label for the submit button on the Replace Snapshot dialog',
    },
    cancelButtonAriaLabel: {
        id: 'ReplaceSnapshotDialog_CancelButton_AriaLabel',
        defaultMessage: 'Cancel',
        description: 'Aria label for the cancel button on the Replace Snapshot dialog.',
    },
    closeButtonAriaLabel: {
        id: 'ReplaceSnapshotDialog_CloseButton_Text',
        defaultMessage: 'Close',
        description: 'Aria label for the "x" button in the top corner of the ReplaceSnapshot dialog',
    },
});

/**
 * Styles
 */
const dialogWidth = '520px';
const dialogStyle: React.CSSProperties = {
    width: dialogWidth,
};

const useDialogStackStyles = makeStyles({
    root: {
        fontWeight: 'normal',
        gap: '18px',
        padding: '0px 0px 30px 0px',
    },
});

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        width: '100%',
        maxWidth: '400px',
    },
});

/**
 * End Styles
 */

const ReplaceSnapshotDialog: React.FC<ReplaceSnapshotDialogProps> = ({
    onDismiss,
    onCancel,
    onReplaceSnapshotSubmitted,
    open,
    existingManualSnapshotName,
    newSnapshotName,
}) => {
    const snapshotId = useId('input');

    const theme = useCurrentFluent2Theme();
    const { formatMessage } = useIntl();
    const stackStyles = useStackStyles();
    const dialogStackStyles = useDialogStackStyles();
    const styles = useStyles();

    const onSubmit = React.useCallback(() => {
        onReplaceSnapshotSubmitted();
        onCancel();
    }, [onReplaceSnapshotSubmitted, onCancel]);

    return (
        <FluentProvider theme={theme}>
            <Dialog
                modalType="alert"
                open={open}
                onOpenChange={(isOpen) => {
                    if (!isOpen) {
                        onDismiss();
                    }
                }}
            >
                <DialogSurface style={dialogStyle}>
                    <DialogBody>
                        <DialogTitle
                            action={
                                <Button
                                    appearance="subtle"
                                    aria-label={formatMessage(messages.closeButtonAriaLabel)}
                                    icon={<Dismiss24Regular />}
                                    onClick={onDismiss}
                                />
                            }
                        >
                            <FormattedMessage
                                id="ReplaceSnapshotDialog_Title"
                                defaultMessage="Replace snapshot?"
                                description="The title for the dialog describing the Replace Snapshot."
                            />
                        </DialogTitle>
                        <DialogContent>
                            <div className={mergeClasses(stackStyles.root, dialogStackStyles.root)}>
                                <div className={stackStyles.item}>
                                    <FormattedMessage
                                        id="ReplaceSnapshotDialog_Description"
                                        defaultMessage="This snapshot will replace the existing snapshot ({existingManualSnapshotName}). The dev box will be unavailable for up to 10 minutes while the snapshot is being taken."
                                        description="Replace Snapshot dialog description."
                                        values={{ existingManualSnapshotName }}
                                    />
                                </div>
                                <div className={styles.root}>
                                    <Label htmlFor={snapshotId}>
                                        <FormattedMessage
                                            id="ReplaceSnapshotDialog_SnapshotName_Label"
                                            defaultMessage="Snapshot name"
                                            description="Label for the snapshot name input field in the Replace Snapshot dialog"
                                        />
                                    </Label>
                                    <Input value={newSnapshotName} id={snapshotId} readOnly />
                                </div>
                            </div>
                        </DialogContent>
                        <DialogActions>
                            <Button
                                appearance="primary"
                                aria-label={formatMessage(messages.replaceSnapshotButtonAriaLabel)}
                                onClick={onSubmit}
                            >
                                <FormattedMessage
                                    id="ReplaceSnapshotDialog_SubmitButton_Label"
                                    defaultMessage="Replace snapshot"
                                    description="Label for the submit button on the replace snapshot dialog."
                                />
                            </Button>
                            <Button
                                appearance="secondary"
                                aria-label={formatMessage(messages.cancelButtonAriaLabel)}
                                onClick={onCancel}
                            >
                                <FormattedMessage
                                    id="ReplaceSnapshotDialog_CancelButton_Label"
                                    defaultMessage="Cancel"
                                    description="Label for the cancel button on the Replace Snapshot dialog."
                                />
                            </Button>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
        </FluentProvider>
    );
};

export default ReplaceSnapshotDialog;
